import { useState, useRef } from 'react';
import {
  X,
  ShoppingCart,
  Download,
  Check,
  Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── Categorii și cuvinte cheie ────────────────────────────────────────────────
const CATEGORIES: {
  id: string;
  label: string;
  emoji: string;
  color: string;
  bgColor: string;
  borderColor: string;
  keywords: string[];
}[] = [
  {
    id: 'legume',
    label: 'Legume',
    emoji: '🥦',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    keywords: [
      'morcov', 'morcovi', 'broccoli', 'spanac', 'salată', 'roșie', 'roșii', 'tomate',
      'ardei', 'castraveți', 'castravete', 'ceapă', 'usturoi', 'dovlecel', 'dovleac',
      'vinete', 'varză', 'conopidă', 'fasole verde', 'mazăre', 'porumb', 'sfeclă',
      'țelină', 'praz', 'ridichi', 'sparanghel', 'anghinare', 'kale', 'rucola',
      'pătrunjel', 'mărar', 'busuioc', 'coriandru', 'mentă', 'cimbru', 'rozmarin',
      'leuștean', 'lobodă', 'stevie', 'nap', 'gulie', 'broccolini', 'bok choy',
      'fenicul', 'andive', 'cicoare', 'verdețuri', 'legume', 'vegetale', 'zucchini',
      'cartof', 'cartofi', 'cartof dulce', 'sweet potato', 'yam',
      'pepper', 'tomato', 'onion', 'garlic', 'carrot', 'spinach', 'lettuce',
      'cucumber', 'cabbage', 'cauliflower', 'eggplant', 'mushroom', 'ciuperci',
      'champignon', 'shiitake', 'portobello', 'potato', 'potatoes'
    ]
  },
  {
    id: 'fructe',
    label: 'Fructe',
    emoji: '🍎',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    keywords: [
      'măr', 'mere', 'banană', 'banane', 'portocală', 'portocale', 'lămâie', 'lămâi',
      'lime', 'grapefruit', 'kiwi', 'mango', 'ananas', 'căpșuni', 'căpșune',
      'afine', 'zmeură', 'mure', 'cireșe', 'vișine', 'piersici', 'piersică',
      'nectarine', 'prune', 'pere', 'pară', 'struguri', 'pepene', 'papaya',
      'avocado', 'smochine', 'curmale', 'stafide', 'coacăze', 'agrișe', 'fructe',
      'apple', 'banana', 'orange', 'lemon', 'strawberry', 'blueberry', 'raspberry',
      'cherry', 'peach', 'grape', 'watermelon', 'pineapple', 'coconut', 'cocos',
      'nuca de cocos', 'fructe de pădure', 'berry', 'berries'
    ]
  },
  {
    id: 'proteine',
    label: 'Carne & Proteine',
    emoji: '🥩',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    keywords: [
      'pui', 'piept de pui', 'pulpe de pui', 'carne de pui', 'curcan', 'vită',
      'carne de vită', 'porc', 'carne de porc', 'miel', 'vițel', 'rață', 'gâscă',
      'iepure', 'carne tocată', 'mușchi', 'cotlet', 'antricot', 'friptură',
      'șuncă', 'bacon', 'cârnați', 'salam', 'mezeluri', 'prosciutto', 'jambon',
      'chicken', 'beef', 'pork', 'turkey', 'lamb', 'meat', 'steak', 'mince',
      'ground beef', 'ham', 'sausage', 'tofu', 'tempeh', 'seitan', 'edamame',
      'proteină', 'proteine'
    ]
  },
  {
    id: 'peste',
    label: 'Pește & Fructe de Mare',
    emoji: '🐟',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    keywords: [
      'somon', 'ton', 'cod', 'tilapia', 'doradă', 'biban', 'crap', 'păstrăv',
      'hering', 'macrou', 'sardine', 'anșoa', 'pește', 'creveți', 'crevete',
      'calamari', 'midii', 'scoici', 'homar', 'crab', 'caracatiță', 'icre',
      'salmon', 'tuna', 'shrimp', 'fish', 'seafood', 'cod fish', 'trout',
      'sea bass', 'anchovy', 'sardine', 'mackerel', 'herring'
    ]
  },
  {
    id: 'oua_lactate',
    label: 'Ouă & Lactate',
    emoji: '🥛',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    keywords: [
      'ouă', 'ou', 'albuș', 'albușuri', 'gălbenuș', 'lapte', 'iaurt', 'brânză',
      'telemea', 'caș', 'urdă', 'smântână', 'frișcă', 'unt', 'kefir', 'skyr',
      'mozzarella', 'parmezan', 'cheddar', 'brie', 'camembert', 'ricotta',
      'mascarpone', 'cottage cheese', 'cremă de brânză', 'brânză cottage',
      'lapte de cocos', 'lapte de migdale', 'lapte de ovăz', 'lapte vegetal',
      'egg', 'eggs', 'egg white', 'milk', 'yogurt', 'greek yogurt', 'cheese',
      'butter', 'cream', 'dairy', 'whey', 'cazeină', 'casein', 'proteină din zer',
      'whey protein', 'proteină'
    ]
  },
  {
    id: 'cereale',
    label: 'Cereale & Pâine',
    emoji: '🌾',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    keywords: [
      'orez', 'paste', 'pâine', 'pâine integrală', 'fulgi de ovăz', 'ovăz',
      'quinoa', 'hrișcă', 'mei', 'orz', 'secară', 'grâu', 'făină', 'mălai',
      'mămăligă', 'couscous', 'bulgur', 'spelt', 'amaranth', 'teff',
      'cereale', 'granola', 'musli', 'biscuiți', 'crackers', 'tortilla',
      'chiflă', 'chifle', 'baghetă', 'lipie', 'lavash',
      'rice', 'pasta', 'bread', 'oats', 'flour', 'wheat', 'barley', 'rye',
      'noodles', 'spaghetti', 'penne', 'fusilli', 'tagliatelle', 'lasagna',
      'wrap', 'pita', 'bagel', 'croissant', 'roll', 'bun'
    ]
  },
  {
    id: 'leguminoase',
    label: 'Leguminoase & Nuci',
    emoji: '🫘',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    keywords: [
      'fasole', 'linte', 'năut', 'mazăre uscată', 'soia', 'boabe', 'leguminoase',
      'nuci', 'nucă', 'migdale', 'alune', 'caju', 'fistic', 'macadamia',
      'semințe de floarea soarelui', 'semințe de dovleac', 'semințe de chia',
      'semințe de in', 'semințe de susan', 'semințe', 'unt de arahide',
      'unt de migdale', 'tahini', 'arahide', 'mix de nuci', 'mix nuci',
      'beans', 'lentils', 'chickpeas', 'nuts', 'almonds', 'walnuts', 'cashew',
      'peanuts', 'peanut butter', 'almond butter', 'seeds', 'chia', 'flaxseed',
      'sunflower seeds', 'pumpkin seeds', 'sesame', 'hemp seeds'
    ]
  },
  {
    id: 'uleiuri_condimente',
    label: 'Uleiuri & Condimente',
    emoji: '🫙',
    color: 'text-lime-700',
    bgColor: 'bg-lime-50',
    borderColor: 'border-lime-200',
    keywords: [
      'ulei de măsline', 'ulei de cocos', 'ulei de floarea soarelui', 'ulei',
      'oțet', 'oțet balsamic', 'sos de soia', 'tamari', 'miso', 'worcestershire',
      'muștar', 'ketchup', 'maioneză', 'sos', 'pastă de tomate', 'bulion',
      'sare', 'piper', 'boia', 'turmeric', 'curcuma', 'ghimbir', 'scorțișoară',
      'cardamom', 'cumin', 'chimen', 'coriandru', 'oregano', 'thyme', 'curry',
      'paprika', 'chili', 'cayenne', 'nucșoară', 'vanilie', 'condimente', 'mirodenii',
      'miere', 'sirop de arțar', 'stevia', 'zahăr', 'cacao', 'pudră de cacao',
      'extract de vanilie', 'drojdie', 'praf de copt', 'bicarbonat',
      'olive oil', 'coconut oil', 'oil', 'vinegar', 'soy sauce', 'salt', 'pepper',
      'spices', 'herbs', 'seasoning', 'sauce', 'paste', 'tomato paste',
      'honey', 'maple syrup', 'cocoa', 'cinnamon', 'cumin', 'paprika'
    ]
  },
  {
    id: 'bauturi',
    label: 'Băuturi',
    emoji: '💧',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    keywords: [
      'apă', 'ceai', 'cafea', 'suc', 'smoothie', 'lapte', 'băutură', 'băuturi',
      'apă minerală', 'apă plată', 'kombucha', 'kefir de apă', 'limonadă',
      'water', 'tea', 'coffee', 'juice', 'drink', 'beverage', 'broth', 'supă',
      'zeamă', 'bulion lichid', 'stock'
    ]
  },
  {
    id: 'altele',
    label: 'Altele',
    emoji: '📦',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    keywords: []
  }
];

// ─── Funcție de categorizare ───────────────────────────────────────────────────
function categorizeItems(items: string[]): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  CATEGORIES.forEach(cat => { result[cat.id] = []; });

  items.forEach(item => {
    const itemLower = item.toLowerCase().trim();
    let assigned = false;

    for (const cat of CATEGORIES) {
      if (cat.id === 'altele') continue;
      for (const kw of cat.keywords) {
        if (itemLower.includes(kw) || kw.includes(itemLower)) {
          result[cat.id].push(item);
          assigned = true;
          break;
        }
      }
      if (assigned) break;
    }

    if (!assigned) {
      result['altele'].push(item);
    }
  });

  // Elimină categoriile goale (cu excepția "altele" dacă are items)
  return result;
}

// ─── Normalizează un ingredient: elimină cantități și unități ─────────────────
function normalizeIngredient(raw: string): string {
  return raw
    // elimină cantități de tipul "150g", "2 buc", "1/2 cană", "300 ml", etc.
    .replace(/^\d+[\d.,/]*\s*(g|kg|ml|l|buc|bucăți|bucati|cana|căni|cani|linguri|lingura|lingurite|linguriță|lingurita|felie|felii|plic|plicuri|porție|portie|portii|porții|cup|cups|tbsp|tsp|oz|lb|piece|pieces)\.?\s*/i, '')
    // elimină cifre singure la început ("2 ouă" → "ouă")
    .replace(/^\d+[\d.,/]*\s+/, '')
    .trim()
    .toLowerCase();
}

// ─── Extrage ingredientele din planul de mese ─────────────────────────────────
// Prioritate: shopping_list de la AI (deja curată) > ingrediente din mese (cu normalizare)
function extractAllIngredients(planData: any): string[] {
  // 1. Dacă AI-ul a generat shopping_list → folosim DOAR aceea (e deja deduplicată și curată)
  if (planData?.shopping_list && Array.isArray(planData.shopping_list) && planData.shopping_list.length > 0) {
    const seen = new Set<string>();
    const result: string[] = [];
    planData.shopping_list.forEach((item: string) => {
      const trimmed = item?.trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(trimmed);
      }
    });
    return result.sort();
  }

  // 2. Fallback: extrage din ingredientele meselor cu normalizare și deduplicare
  const seen = new Map<string, string>(); // key normalizat → valoare originală

  if (planData?.days && Array.isArray(planData.days)) {
    planData.days.forEach((day: any) => {
      ['breakfast', 'lunch', 'dinner'].forEach(mealType => {
        const meal = day[mealType];
        if (meal?.ingredients && Array.isArray(meal.ingredients)) {
          meal.ingredients.forEach((ing: string) => {
            const trimmed = ing?.trim();
            if (!trimmed) return;
            const normalized = normalizeIngredient(trimmed);
            if (normalized && !seen.has(normalized)) {
              // Păstrăm versiunea originală (fără cantitate) ca display
              const display = trimmed.replace(/^\d+[\d.,/]*\s*(g|kg|ml|l|buc|bucăți|bucati|cana|căni|cani|linguri|lingura|lingurite|linguriță|lingurita|felie|felii|plic|plicuri|porție|portie|portii|porții|cup|cups|tbsp|tsp|oz|lb|piece|pieces)\.?\s*/i, '').replace(/^\d+[\d.,/]*\s+/, '').trim();
              seen.set(normalized, display || trimmed);
            }
          });
        }
      });
    });
  }

  return Array.from(seen.values()).sort();
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface ShoppingListModalProps {
  isOpen: boolean;
  onClose: () => void;
  planData: any;
  planDays: number;
  planDate: string;
}

export default function ShoppingListModal({
  isOpen,
  onClose,
  planData,
  planDays,
  planDate
}: ShoppingListModalProps) {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [exportingPDF, setExportingPDF] = useState(false);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const allIngredients = extractAllIngredients(planData);
  const categorized = categorizeItems(allIngredients);

  // Categorii cu items
  const activeCategories = CATEGORIES.filter(
    cat => categorized[cat.id] && categorized[cat.id].length > 0
  );

  const totalItems = allIngredients.length;
  const checkedCount = checkedItems.size;

  const toggleItem = (item: string) => {
    setCheckedItems(prev => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const toggleCategory = (catId: string) => {
    const catItems = categorized[catId];
    const allChecked = catItems.every(item => checkedItems.has(item));
    setCheckedItems(prev => {
      const next = new Set(prev);
      if (allChecked) {
        catItems.forEach(item => next.delete(item));
      } else {
        catItems.forEach(item => next.add(item));
      }
      return next;
    });
  };

  // ─── Export PDF ─────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const dateStr = new Date(planDate).toLocaleDateString('ro-RO', {
        day: '2-digit', month: 'long', year: 'numeric'
      });
      const fileName = `lista_cumparaturi_${planDays}_zile_${new Date().toISOString().split('T')[0]}.pdf`;

      const renderToCanvas = async (html: string): Promise<HTMLCanvasElement> => {
        const wrapper = document.createElement('div');
        wrapper.style.position = 'fixed';
        wrapper.style.top = '-99999px';
        wrapper.style.left = '-99999px';
        wrapper.style.width = '754px';
        wrapper.style.background = '#ffffff';
        wrapper.style.fontFamily = 'Arial, Helvetica, sans-serif';
        wrapper.style.color = '#111827';
        wrapper.innerHTML = html;
        document.body.appendChild(wrapper);
        await new Promise(r => setTimeout(r, 80));
        const c = await html2canvas(wrapper, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 754,
          windowWidth: 754
        });
        document.body.removeChild(wrapper);
        return c;
      };

      const headerHtml = `
        <div style="background:linear-gradient(135deg,#16a34a,#2563eb);border-radius:16px;padding:24px;color:#fff;text-align:center;margin-bottom:16px;">
          <div style="font-size:36px;margin-bottom:8px;">🛒</div>
          <h1 style="font-size:22px;font-weight:800;margin:0 0 6px;">Listă de Cumpărături</h1>
          <p style="font-size:12px;opacity:0.9;margin:0;">Plan ${planDays} Zile · Generat pe ${dateStr}</p>
          <div style="margin-top:14px;display:inline-block;background:rgba(255,255,255,0.2);border-radius:10px;padding:8px 20px;">
            <span style="font-size:18px;font-weight:800;">${totalItems}</span>
            <span style="font-size:11px;opacity:0.85;margin-left:4px;">produse totale</span>
          </div>
        </div>`;

      const categoriesHtml = activeCategories.map(cat => `
        <div style="background:#fff;border-radius:12px;box-shadow:0 2px 6px rgba(0,0,0,0.07);padding:16px;margin-bottom:12px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #f3f4f6;">
            <span style="font-size:20px;">${cat.emoji}</span>
            <span style="font-size:14px;font-weight:700;color:#111827;">${cat.label}</span>
            <span style="margin-left:auto;background:#f3f4f6;color:#6b7280;padding:2px 8px;border-radius:20px;font-size:11px;font-weight:600;">${categorized[cat.id].length} produse</span>
          </div>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:6px;">
            ${categorized[cat.id].map(item => `
              <div style="display:flex;align-items:center;gap:6px;padding:6px 8px;background:#f9fafb;border-radius:8px;border:1px solid #e5e7eb;">
                <div style="width:14px;height:14px;border:2px solid #d1d5db;border-radius:3px;flex-shrink:0;"></div>
                <span style="font-size:10px;color:#374151;">${item}</span>
              </div>`).join('')}
          </div>
        </div>`).join('');

      const footerHtml = `
        <div style="text-align:center;padding:14px;color:#9ca3af;font-size:10px;border-top:1px solid #e5e7eb;margin-top:8px;">
          Generat de <strong style="color:#16a34a;">FII FIT</strong> · Lista de cumpărături personalizată · ${dateStr}
        </div>`;

      const blocks = [
        await renderToCanvas(headerHtml),
        await renderToCanvas(categoriesHtml),
        await renderToCanvas(footerHtml)
      ];

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const PAGE_W = 210, PAGE_H = 297, MARGIN = 10;
      const CONTENT_W = PAGE_W - MARGIN * 2;
      const CONTENT_H = PAGE_H - MARGIN * 2;
      const PX_TO_MM = CONTENT_W / (754 * 2);
      const GAP = 4;
      let curY = MARGIN;

      for (const canvas of blocks) {
        const blockH = canvas.height * PX_TO_MM;
        const spaceLeft = CONTENT_H - (curY - MARGIN);

        if (blockH > CONTENT_H) {
          let sliceStartPx = 0;
          let remainingPx = canvas.height;
          while (remainingPx > 0) {
            const spaceLeftMM = CONTENT_H - (curY - MARGIN);
            const sliceHeightPx = Math.min(Math.ceil(spaceLeftMM / PX_TO_MM), remainingPx);
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceHeightPx;
            const ctx = sliceCanvas.getContext('2d');
            if (ctx) ctx.drawImage(canvas, 0, sliceStartPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
            const imgData = sliceCanvas.toDataURL('image/jpeg', 0.92);
            const actualH = sliceCanvas.height * PX_TO_MM;
            pdf.addImage(imgData, 'JPEG', MARGIN, curY, CONTENT_W, actualH);
            sliceStartPx += sliceHeightPx;
            remainingPx -= sliceHeightPx;
            curY += actualH + GAP;
            if (remainingPx > 0) { pdf.addPage(); curY = MARGIN; }
          }
        } else {
          if (blockH > spaceLeft && curY > MARGIN) { pdf.addPage(); curY = MARGIN; }
          const imgData = canvas.toDataURL('image/jpeg', 0.92);
          pdf.addImage(imgData, 'JPEG', MARGIN, curY, CONTENT_W, blockH);
          curY += blockH + GAP;
        }
      }

      const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
      if (isCapacitor) {
        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
        const shareApi = navigator as any;
        if (typeof shareApi.canShare === 'function' && shareApi.canShare({ files: [pdfFile] })) {
          await shareApi.share({ title: `Listă Cumpărături ${planDays} Zile - FII FIT`, files: [pdfFile] });
        } else {
          pdf.save(fileName);
        }
      } else {
        pdf.save(fileName);
      }
    } catch (err) {
      console.error('Eroare PDF:', err);
      alert('A apărut o eroare la generarea PDF-ului. Încearcă din nou.');
    } finally {
      setExportingPDF(false);
    }
  };

  // ─── Share WhatsApp ──────────────────────────────────────────────────────────
  const handleShareWhatsApp = () => {
    setSharingWhatsApp(true);
    try {
      const dateStr = new Date(planDate).toLocaleDateString('ro-RO', {
        day: '2-digit', month: 'long', year: 'numeric'
      });

      let message = `🛒 *Listă de Cumpărături - Plan ${planDays} Zile*\n`;
      message += `📅 Generat pe ${dateStr} cu FII FIT\n`;
      message += `📦 Total: ${totalItems} produse\n\n`;

      activeCategories.forEach(cat => {
        const items = categorized[cat.id];
        if (items.length === 0) return;
        message += `${cat.emoji} *${cat.label.toUpperCase()}*\n`;
        items.forEach(item => {
          message += `  ☐ ${item}\n`;
        });
        message += '\n';
      });

      message += `\n✨ _Generat cu FII FIT - Aplicația ta de fitness_\n_https://fiifit.ro_`;

      const encoded = encodeURIComponent(message);
      const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();

      if (isCapacitor) {
        window.open(`whatsapp://send?text=${encoded}`, '_blank');
      } else {
        window.open(`https://wa.me/?text=${encoded}`, '_blank');
      }
    } catch (err) {
      console.error('Eroare WhatsApp:', err);
    } finally {
      setSharingWhatsApp(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Listă de Cumpărături</h2>
              <p className="text-xs text-gray-500">
                {totalItems} produse · Plan {planDays} zile
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress bar */}
        {totalItems > 0 && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-600">Produse bifate</span>
              <span className="text-xs font-bold text-green-600">{checkedCount}/{totalItems}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${totalItems > 0 ? (checkedCount / totalItems) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Content - scrollable */}
        <div ref={contentRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {activeCategories.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nu s-au găsit ingrediente în planul de mese.</p>
            </div>
          ) : (
            activeCategories.map(cat => {
              const items = categorized[cat.id];
              const allCatChecked = items.every(item => checkedItems.has(item));
              const someCatChecked = items.some(item => checkedItems.has(item));

              return (
                <div key={cat.id} className={`rounded-xl border-2 ${cat.borderColor} overflow-hidden`}>
                  {/* Category header */}
                  <button
                    onClick={() => toggleCategory(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 ${cat.bgColor} hover:opacity-90 transition-opacity`}
                  >
                    <span className="text-xl">{cat.emoji}</span>
                    <span className={`font-bold text-sm flex-1 text-left ${cat.color}`}>
                      {cat.label}
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-white/70 ${cat.color}`}>
                      {items.length} produse
                    </span>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      allCatChecked
                        ? 'bg-green-500 border-green-500'
                        : someCatChecked
                        ? 'bg-green-200 border-green-400'
                        : 'bg-white border-gray-300'
                    }`}>
                      {(allCatChecked || someCatChecked) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                  </button>

                  {/* Items grid */}
                  <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {items.map((item, idx) => {
                      const isChecked = checkedItems.has(item);
                      return (
                        <button
                          key={idx}
                          onClick={() => toggleItem(item)}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all text-left ${
                            isChecked
                              ? 'bg-green-50 border-green-200 opacity-60'
                              : 'bg-white border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isChecked ? 'bg-green-500 border-green-500' : 'border-gray-300'
                          }`}>
                            {isChecked && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className={`text-sm ${isChecked ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                            {item}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <div className="flex gap-3">
            {/* WhatsApp */}
            <button
              onClick={handleShareWhatsApp}
              disabled={sharingWhatsApp || totalItems === 0}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white rounded-xl font-semibold text-sm hover:bg-[#20bd5a] disabled:opacity-50 transition-all shadow-sm"
            >
              {sharingWhatsApp ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              )}
              <span>WhatsApp</span>
            </button>

            {/* PDF */}
            <button
              onClick={handleExportPDF}
              disabled={exportingPDF || totalItems === 0}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl font-semibold text-sm hover:shadow-md disabled:opacity-50 transition-all shadow-sm"
            >
              {exportingPDF ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{exportingPDF ? 'Generez PDF...' : 'Export PDF'}</span>
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-2">
            Bifează produsele pe măsură ce le cumperi
          </p>
        </div>
      </div>
    </div>
  );
}
